import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Locale } from "@ec2/i18n";
import { I18nProvider } from "@ec2/i18n/react";
import { createApiClient, TRPCProvider, useTRPC, type ApiClient } from "../lib/api";
import { createAuthApi, ensureSession, type AuthApi } from "../lib/auth";
import { appConfig } from "../lib/config";
import { detectLocale, persistLocale } from "../lib/locale";
import { existingPushSubscription, markInstallReported, shouldReportInstall } from "../lib/pwa";

/** Injectable app dependencies (tests swap fetch for the in-process API). */
export interface AppDeps {
  api: ApiClient;
  auth: AuthApi;
  queryClient: QueryClient;
}

export function createAppDeps(overrides: Partial<AppDeps> = {}): AppDeps {
  return {
    api: overrides.api ?? createApiClient({ baseUrl: appConfig.apiBase }),
    auth: overrides.auth ?? createAuthApi(),
    queryClient:
      overrides.queryClient ??
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, staleTime: 5_000, refetchOnWindowFocus: true },
        },
      }),
  };
}

type SessionStatus = "booting" | "ready" | "error";

export interface SessionValue {
  status: SessionStatus;
  api: ApiClient;
  auth: AuthApi;
  locale: Locale;
  setLocale: (locale: Locale, opts?: { persist?: boolean }) => void;
  /** Re-run the auth bootstrap after a failure. */
  retryBoot: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession outside AppProviders");
  return value;
}

/** participant.me — the app's core query (session user + active participation). */
export function useMe() {
  const trpc = useTRPC();
  const { status } = useSession();
  return useQuery({ ...trpc.participant.me.queryOptions(), enabled: status === "ready" });
}

function SessionBoundary({ deps, children }: { deps: AppDeps; children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("booting");
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());
  const [bootAttempt, setBootAttempt] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    setStatus("booting");
    // bootAttempt > 0 means a human pressed retry, which is allowed to redirect
    // to Midway again immediately (see the loop breaker in lib/auth.ts).
    ensureSession(deps.auth, { force: bootAttempt > 0 })
      .then((result) => {
        // "redirecting": the Midway navigation is already in flight, so stay in
        // the booting state — a flash of an error screen before the tab is
        // replaced would look like a failure that never happened.
        if (cancelled || result === "redirecting") return;
        setStatus(result === "ready" ? "ready" : "error");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [deps.auth, bootAttempt]);

  const setLocale = useCallback(
    (next: Locale, opts: { persist?: boolean } = {}) => {
      setLocaleState(next);
      // Persist a player's choice only; an adopted account locale is not one.
      if (opts.persist !== false) persistLocale(next);
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const value = useMemo<SessionValue>(
    () => ({
      status,
      api: deps.api,
      auth: deps.auth,
      locale,
      setLocale,
      retryBoot: () => setBootAttempt((n) => n + 1),
    }),
    [status, deps.api, deps.auth, locale, setLocale],
  );

  return (
    <SessionContext.Provider value={value}>
      <I18nProvider locale={locale}>{children}</I18nProvider>
    </SessionContext.Provider>
  );
}

/**
 * Side effects tied to the active participation:
 * - adopt the server-persisted locale once known,
 * - report a standalone launch, once per launch: it solves the event's install
 *   quest (and records the launch as telemetry),
 * - re-send an existing push subscription, once per launch: it solves the
 *   event's notifications quest for players who turned push on beforehand.
 */
/** Events whose push subscription was already re-sent during this launch. */
const resentPushThisLaunch = new Set<string>();

export function SessionEffects() {
  const me = useMe();
  const queryClient = useQueryClient();
  const { api, locale, setLocale } = useSession();
  const adoptedLocale = useRef(false);
  const active = me.data?.active ?? null;

  useEffect(() => {
    const serverLocale = me.data?.user.locale;
    // "en" is the default every new account starts with — it says nothing about
    // the player, so it must not override the device language. Adopting it was
    // why German phones played in English with English names (prod,
    // 2026-10-21: 83 of 84 anonymous accounts "en"). Only a language the
    // player picked (profile → stored on the account) is carried over.
    if (!adoptedLocale.current && serverLocale && serverLocale !== "en" && serverLocale !== locale) {
      adoptedLocale.current = true;
      setLocale(serverLocale, { persist: false });
    } else if (serverLocale) {
      adoptedLocale.current = true;
    }
  }, [me.data?.user.locale, locale, setLocale]);

  useEffect(() => {
    if (!active) return;
    if (!shouldReportInstall(active.eventId)) return;
    markInstallReported(active.eventId);
    void api.participant.reportPwaInstalled
      .mutate({ eventId: active.eventId })
      .then(async (r) => {
        if (r.bucksAwarded > 0) await queryClient.invalidateQueries();
        else await me.refetch();
      })
      .catch(() => {
        /* best effort — retried on the next launch */
      });
  }, [active?.eventId, api]);

  useEffect(() => {
    if (!active || resentPushThisLaunch.has(active.eventId)) return;
    resentPushThisLaunch.add(active.eventId);
    const eventId = active.eventId;
    void existingPushSubscription()
      .then((subscription) => (subscription ? api.push.subscribe.mutate({ eventId, ...subscription }) : null))
      .then((r) => (r && r.bucksAwarded > 0 ? queryClient.invalidateQueries() : undefined))
      .catch(() => {
        /* best effort */
      });
  }, [active?.eventId, api]);

  return null;
}

export function AppProviders({ deps, children }: { deps: AppDeps; children: ReactNode }) {
  return (
    <QueryClientProvider client={deps.queryClient}>
      <TRPCProvider trpcClient={deps.api} queryClient={deps.queryClient}>
        <SessionBoundary deps={deps}>
          <SessionEffects />
          {children}
        </SessionBoundary>
      </TRPCProvider>
    </QueryClientProvider>
  );
}
